import { useCallback, useMemo, useState } from "react";
import type { SetURLSearchParams } from "react-router-dom";

const KEYS = {
	project: "project",
	sprint: "sprint",
	user: "assignee",
	status: "status",
	search: "q",
	view: "view",
} as const;

// `view` is intentionally excluded: "Clear filters" must not reset the view.
const FILTER_KEYS = [
	KEYS.project,
	KEYS.sprint,
	KEYS.user,
	KEYS.status,
	KEYS.search,
];

export type TaskView = "board" | "list" | "calendar";

const VIEWS: TaskView[] = ["board", "list", "calendar"];
const VIEW_KEY = "tasks:view";

/**
 * Last view the user picked, else narrow screens default to the list since
 * the board needs horizontal room.
 */
function defaultView(): TaskView {
	try {
		const saved = localStorage.getItem(VIEW_KEY) as TaskView | null;
		if (saved && VIEWS.includes(saved)) return saved;
	} catch {
		// ignore storage failures
	}
	return typeof window !== "undefined" && window.innerWidth < 768
		? "list"
		: "board";
}

/**
 * Backs the task filters with the URL query string so applied filters survive
 * reloads and are deep-linkable. Reuses the page's `useSearchParams` instance
 * to avoid two instances racing on writes. Default/empty values are stripped.
 */
export function useTaskFilters(
	searchParams: URLSearchParams,
	setSearchParams: SetURLSearchParams,
	// Sprint used while the URL has no sprint param (the active sprint).
	defaultSprint = "all",
) {
	const makeSetter = useCallback(
		(key: string, empty: string) => (value: string) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (!value || value === empty) next.delete(key);
					else next.set(key, value);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const setFilterProject = useMemo(
		() => makeSetter(KEYS.project, "all"),
		[makeSetter],
	);
	// "all" is kept in the URL: an absent param means "default sprint".
	const setFilterSprint = useMemo(
		() => makeSetter(KEYS.sprint, ""),
		[makeSetter],
	);
	const setFilterUserIds = useCallback(
		(ids: string[]) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (ids.length === 0) next.delete(KEYS.user);
					else next.set(KEYS.user, ids.join(","));
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);
	const setFilterStatus = useMemo(
		() => makeSetter(KEYS.status, "all"),
		[makeSetter],
	);
	const setSearch = useMemo(
		() => makeSetter(KEYS.search, ""),
		[makeSetter],
	);
	const setView = useCallback(
		(v: TaskView) => {
			try {
				localStorage.setItem(VIEW_KEY, v);
			} catch {
				// ignore storage failures
			}
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set(KEYS.view, v);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const clearFilters = useCallback(() => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				FILTER_KEYS.forEach((k) => next.delete(k));
				return next;
			},
			{ replace: true },
		);
	}, [setSearchParams]);

	const filterUserIds = useMemo(() => {
		const raw = searchParams.get(KEYS.user);
		return raw ? raw.split(",").filter(Boolean) : [];
	}, [searchParams]);

	// Resolved once so an unknown/absent param falls back consistently instead
	// of flipping when the window is resized mid-session.
	const [fallbackView] = useState(defaultView);
	const rawView = searchParams.get(KEYS.view) as TaskView | null;
	const view =
		rawView && VIEWS.includes(rawView) ? rawView : fallbackView;

	return {
		filterProject: searchParams.get(KEYS.project) ?? "all",
		setFilterProject,
		filterSprint: searchParams.get(KEYS.sprint) ?? defaultSprint,
		setFilterSprint,
		filterUserIds,
		setFilterUserIds,
		filterStatus: searchParams.get(KEYS.status) ?? "all",
		setFilterStatus,
		search: searchParams.get(KEYS.search) ?? "",
		setSearch,
		view,
		setView,
		clearFilters,
	};
}
